import { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CircleHelpIcon, InfoIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ToolkitEditor } from '@/components/organisms/Toolkit/ToolkitEditor/ToolkitEditor';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormAutocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import useDialogAlert from '@/hooks/useDialogAlert';
import useDialogs from '@/hooks/useDialogs';
import { selectHasError } from '@/redux/dialogAlerts/selectors';
import {
  selectNodeIdByWhatsAppFlowAction,
  selectWhatsAppFlowAction,
} from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { WhatsAppFlowAction, WhatsAppFlowActionPayload } from '@/shared/types/action';
import { getFormErrorsKey } from '@/util/util';
import { whatsappFlowSchema } from '@/util/validation/schemas/whatsapp-schemas';

import ToolkitWrapper from '../../ToolkitWrapper';

type FormType = {
  flow_id: string;
  flow_body: string;
  flow_cta: string;
  flow_action: 'navigate' | 'data_exchange';
  flow_header: string;
  flow_footer: string;
  screen: string;
  initial_data: string;
  trigger_node_id: string;
};

type SimpleFlowField = 'flow_id';

const parseInitialData = (payload?: WhatsAppFlowActionPayload): string => {
  if (!payload?.data || Object.keys(payload.data).length === 0) {
    return '';
  }
  return JSON.stringify(payload.data, null, 2);
};

const getFormDefaults = (action?: WhatsAppFlowAction): FormType => ({
  flow_id: action?.whatsapp_flow?.flow_id ?? '',
  flow_body: action?.whatsapp_flow?.flow_body ?? '',
  flow_cta: action?.whatsapp_flow?.flow_cta ?? '',
  flow_action: action?.whatsapp_flow?.flow_action ?? 'navigate',
  flow_header: action?.whatsapp_flow?.flow_header ?? '',
  flow_footer: action?.whatsapp_flow?.flow_footer ?? '',
  screen: action?.whatsapp_flow?.flow_action_payload?.screen ?? '',
  initial_data: parseInitialData(action?.whatsapp_flow?.flow_action_payload),
  trigger_node_id: action?.trigger_node_id ?? '',
});

const ToolkitActionFlow = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { brainId, dialogId } = useParams();
  const { detailedOptions } = useDialogs(brainId, dialogId);

  const action = useSelector(selectWhatsAppFlowAction);
  const nodeId = useSelector(selectNodeIdByWhatsAppFlowAction);
  const { variables } = useBrainVariables();

  const form = useForm<FormType>({
    mode: 'onChange',
    resolver: yupResolver(whatsappFlowSchema) as Resolver<FormType>,
    defaultValues: getFormDefaults(action),
  });

  const {
    getValues,
    setValue,
    control,
    trigger,
    reset,
    formState: { errors },
  } = form;

  // Reset form when switching to a different action
  useEffect(() => {
    if (action) {
      reset(getFormDefaults(action));
    }
  }, [action?.action_id, reset]);

  const watchFlowAction = useWatch({ control, name: 'flow_action' });

  const { setFieldError } = useDialogAlert({
    id: action?.action_id ?? '',
    nodeId: nodeId ?? '',
    title: t('actions.types.whatsapp_flow'),
    type: 'whatsapp_flow',
  });

  useEffect(() => {
    const fields = [
      'flow_id',
      'flow_body',
      'flow_cta',
      'flow_header',
      'flow_footer',
      'screen',
      'initial_data',
    ] as const;

    for (const field of fields) {
      setFieldError(field, errors[field]?.message);
    }
  }, [getFormErrorsKey(errors), setFieldError]);
  const hasErrors = useSelector(selectHasError(action?.action_id ?? ''));
  useEffect(() => {
    if (hasErrors) {
      trigger();
    }
  }, [hasErrors, trigger]);

  const transformedOptions = useMemo(
    () =>
      detailedOptions.map((option) => ({
        value: option.value,
        label: option.label,
        category: option.dialog_name,
        disabled: option.value === nodeId,
      })),
    [detailedOptions, nodeId]
  );

  if (!action) {
    return null;
  }

  const dispatchUpdate = (updates: Partial<WhatsAppFlowAction>) => {
    dispatch(updateAction({ actionId: action.action_id, action: updates }));
  };

  const updateWhatsAppFlow = (
    updates: Partial<NonNullable<WhatsAppFlowAction['whatsapp_flow']>>
  ) => {
    dispatchUpdate({ whatsapp_flow: { ...action.whatsapp_flow, ...updates } });
  };

  const handleSimpleFieldBlur = async (field: SimpleFlowField) => {
    await trigger(field);
    updateWhatsAppFlow({ [field]: getValues(field) || undefined });
  };

  const handlePayloadFieldBlur = async () => {
    const [isScreenValid, isDataValid] = await Promise.all([
      trigger('screen'),
      trigger('initial_data'),
    ]);

    if (!isScreenValid) {
      return;
    }

    const screen = getValues('screen');
    const initialData = getValues('initial_data').trim();

    let data: Record<string, unknown> | undefined;
    if (isDataValid && initialData) {
      try {
        data = JSON.parse(initialData);
      } catch {
        data = undefined;
      }
    }

    updateWhatsAppFlow({ flow_action_payload: screen ? { screen, data } : undefined });
  };

  const handleActionChange = (value: 'navigate' | 'data_exchange') => {
    setValue('flow_action', value);

    if (value === 'data_exchange') {
      setValue('screen', '');
      setValue('initial_data', '');
      updateWhatsAppFlow({ flow_action: value, flow_action_payload: undefined });
    } else {
      updateWhatsAppFlow({ flow_action: value });
    }
  };

  return (
    <ToolkitWrapper type="whatsapp_flow">
      <Form {...form}>
        <div className="flex flex-col gap-4 pb-6">
          <Alert variant="info" className="text-muted-foreground">
            <InfoIcon size={16} />
            <AlertDescription>{t('dialog.flow.banner')}</AlertDescription>
          </Alert>

          {/* Flow ID */}
          <FormField
            control={control}
            name="flow_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.flow.flow_id')}
                  <InfoTooltip content={t('dialog.flow.flow_id_help')} size="large">
                    <CircleHelpIcon className="size-3.5 text-muted-foreground" />
                  </InfoTooltip>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onBlur={() => {
                      field.onBlur();
                      handleSimpleFieldBlur('flow_id');
                    }}
                    placeholder={t('dialog.flow.flow_id_placeholder')}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>{t('dialog.flow.flow_id_description')}</FormDescription>
              </FormItem>
            )}
          />

          {/* Draft Mode */}
          <FormItem className="flex flex-row justify-between gap-4">
            <div className="space-y-0.5">
              <FormLabel>{t('dialog.flow.draft_mode')}</FormLabel>
              <FormDescription>{t('dialog.flow.draft_mode_description')}</FormDescription>
            </div>
            <div>
              <Switch
                checked={action.whatsapp_flow?.flow_mode === 'draft'}
                onCheckedChange={(checked) =>
                  updateWhatsAppFlow({ flow_mode: checked ? 'draft' : undefined })
                }
              />
            </div>
          </FormItem>

          {/* Message customization section */}
          <p className="flex items-center gap-1 text-sm font-medium">
            {t('dialog.flow.message_customization')}
            <InfoTooltip content={t('dialog.flow.message_customization_help')} size="large">
              <CircleHelpIcon className="size-3.5 text-muted-foreground" />
            </InfoTooltip>
          </p>

          {/* Header */}
          <FormField
            control={control}
            name="flow_header"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.flow.header_optional')}</FormLabel>
                <FormControl>
                  <ToolkitEditor
                    content={field.value}
                    onBlur={(content) => {
                      setValue('flow_header', content, { shouldValidate: true });
                      updateWhatsAppFlow({ flow_header: content || undefined });
                    }}
                    variableOptions={variables}
                    placeholder={t('dialog.flow.header_placeholder')}
                    maxLength={60}
                    size="sm"
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>{t('dialog.flow.header_description')}</FormDescription>
              </FormItem>
            )}
          />

          {/* Message (Body) */}
          <FormField
            control={control}
            name="flow_body"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.flow.message')}
                </FormLabel>
                <FormControl>
                  <ToolkitEditor
                    content={field.value}
                    onBlur={(content) => {
                      setValue('flow_body', content, { shouldValidate: true });
                      updateWhatsAppFlow({ flow_body: content || undefined });
                    }}
                    variableOptions={variables}
                    placeholder={t('dialog.flow.message_placeholder')}
                    maxLength={1024}
                    size="lg"
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>{t('dialog.flow.message_description')}</FormDescription>
              </FormItem>
            )}
          />

          {/* Footer */}
          <FormField
            control={control}
            name="flow_footer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.flow.footer_optional')}</FormLabel>
                <FormControl>
                  <ToolkitEditor
                    content={field.value}
                    onBlur={(content) => {
                      setValue('flow_footer', content, { shouldValidate: true });
                      updateWhatsAppFlow({ flow_footer: content || undefined });
                    }}
                    variableOptions={variables}
                    placeholder={t('dialog.flow.footer_placeholder')}
                    maxLength={60}
                    size="sm"
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>{t('dialog.flow.footer_description')}</FormDescription>
              </FormItem>
            )}
          />

          {/* Button label (CTA) */}
          <FormField
            control={control}
            name="flow_cta"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.flow.button_label')}</FormLabel>
                <FormControl>
                  <ToolkitEditor
                    content={field.value}
                    onBlur={(content) => {
                      setValue('flow_cta', content, { shouldValidate: true });
                      updateWhatsAppFlow({ flow_cta: content || undefined });
                    }}
                    variableOptions={variables}
                    placeholder={t('dialog.flow.button_label_placeholder')}
                    size="sm"
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>{t('dialog.flow.button_label_description')}</FormDescription>
              </FormItem>
            )}
          />

          {/* Trigger Node on completion */}
          <FormField
            control={control}
            name="trigger_node_id"
            render={() => (
              <FormItem>
                <FormLabel className="flex items-center gap-1">
                  {t('dialog.flow.trigger_node')}
                  <InfoTooltip content={t('dialog.flow.trigger_node_help')} size="large">
                    <CircleHelpIcon className="size-3.5 text-muted-foreground" />
                  </InfoTooltip>
                </FormLabel>
                <FormAutocomplete
                  control={control}
                  options={transformedOptions}
                  name="trigger_node_id"
                  placeholder={t('dialog.flow.trigger_node_placeholder')}
                  onChange={(value) => dispatchUpdate({ trigger_node_id: value || undefined })}
                />
                <FormDescription>{t('dialog.flow.trigger_node_description')}</FormDescription>
              </FormItem>
            )}
          />

          {/* Flow Action */}
          <FormField
            control={control}
            name="flow_action"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('dialog.flow.flow_action')}</FormLabel>
                <Select value={field.value} onValueChange={handleActionChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('dialog.flow.flow_action_placeholder')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="navigate">{t('dialog.flow.action_navigate')}</SelectItem>
                    <SelectItem value="data_exchange">
                      {t('dialog.flow.action_data_exchange')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>{t('dialog.flow.flow_action_description')}</FormDescription>
              </FormItem>
            )}
          />

          {watchFlowAction === 'navigate' && (
            <>
              {/* Initial Data JSON */}
              <FormField
                control={control}
                name="initial_data"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      {t('dialog.flow.initial_data_optional')}
                      <InfoTooltip content={t('dialog.flow.initial_data_help')} size="large">
                        <CircleHelpIcon className="size-3.5 text-muted-foreground" />
                      </InfoTooltip>
                    </FormLabel>
                    <FormControl>
                      <ToolkitEditor
                        content={field.value}
                        onBlur={(content) => {
                          setValue('initial_data', content, { shouldValidate: true });
                          handlePayloadFieldBlur();
                        }}
                        variableOptions={variables}
                        placeholder={t('dialog.flow.initial_data_placeholder')}
                        size="md"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Initial Screen */}
              <FormField
                control={control}
                name="screen"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dialog.flow.initial_screen')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onBlur={() => {
                          field.onBlur();
                          handlePayloadFieldBlur();
                        }}
                        placeholder={t('dialog.flow.initial_screen_placeholder')}
                        maxLength={128}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>{t('dialog.flow.initial_screen_description')}</FormDescription>
                  </FormItem>
                )}
              />
            </>
          )}
        </div>
      </Form>
    </ToolkitWrapper>
  );
};

export default ToolkitActionFlow;
